/* eslint-disable */
import * as THREE from '../../../libs/three.js/build/three.module.js';
import { Image360 } from './Image360.js';
import { sphereGeometry, sphereMarkerMaterial } from './constants.js';

// Lazy import to avoid circular dependency
let Images360Class = null;
const getImages360Class = async () => {
  if (!Images360Class) {
    const module = await import('./Images360.js');
    Images360Class = module.Images360;
  }
  return Images360Class;
};

/**
 * Loader dla panoram 360° - obsługuje różne formaty skanerów
 */
export class Images360Loader {
  // Valid scanner formats
  static FORMATS = ['generic', 'navvis', 'leica', 'trimble'];

  /**
   * Main loader method with argument overloading support.
   * Supported signatures:
   * - load(url, viewer)
   * - load(url, viewer, params)
   * - load(url, viewer, params, format)
   * - load(url, viewer, format)
   * - load(url, imagesFileName, images360, viewer, params)
   * - load(url, imagesFileName, images360, viewer, params, format)
   */
  static async load(...args) {
    // Normalize arguments
    let url, imagesFileName, images360, viewer, params, format;

    if (args.length === 2) {
      // load(url, viewer)
      [url, viewer] = args;
      params = {};
      imagesFileName = null;
      images360 = null;
      format = null;
    } else if (args.length === 3) {
      // load(url, viewer, params) OR load(url, viewer, format)
      const [arg0, arg1, arg2] = args;
      url = arg0;

      if (arg1 && typeof arg1 === 'object' && arg1.renderer) {
        viewer = arg1;
        if (typeof arg2 === 'string' && this.FORMATS.includes(arg2)) {
          // load(url, viewer, format)
          params = {};
          format = arg2;
        } else {
          // load(url, viewer, params)
          params = arg2 || {};
          format = null;
        }
        imagesFileName = null;
        images360 = null;
      } else {
        throw new Error('Invalid arguments: second argument must be a viewer object');
      }
    } else if (args.length === 4) {
      // load(url, viewer, params, format)
      [url, viewer, params, format] = args;
      params = params || {};
      imagesFileName = null;
      images360 = null;
    } else if (args.length === 5) {
      // load(url, imagesFileName, images360, viewer, params)
      [url, imagesFileName, images360, viewer, params] = args;
      params = params || {};
      format = null;
    } else if (args.length === 6) {
      // load(url, imagesFileName, images360, viewer, params, format)
      [url, imagesFileName, images360, viewer, params, format] = args;
      params = params || {};
    } else {
      throw new Error(`Invalid number of arguments: ${args.length}. Expected 2-6 arguments.`);
    }

    // Create images360 if not provided
    if (!images360) {
      const Images360 = await getImages360Class();
      images360 = new Images360(viewer);
    }

    // Initialize params.transform
    if (!params.transform) {
      params.transform = {
        forward: (a) => a,
      };
    }

    // Default file name
    if (!imagesFileName || imagesFileName === '') {
      imagesFileName = 'coordinates';
    }

    // Detect format if not specified
    if (!format) {
      format = await this.detectFormat(url, imagesFileName);
    }

    // Validate format
    if (!this.FORMATS.includes(format)) {
      throw new Error(`Invalid format: ${format}. Supported formats: ${this.FORMATS.join(', ')}`);
    }

    // Route to appropriate loader
    let result;
    switch (format) {
      case 'generic':
        result = await this.loadGeneric(url, imagesFileName, images360, viewer, params);
        break;
      case 'navvis':
        result = await this.loadNavvis(url, imagesFileName, images360, viewer, params);
        break;
      case 'leica':
        result = await this.loadLeica(url, imagesFileName, images360, viewer, params);
        break;
      case 'trimble':
        result = await this.loadTrimble(url, images360, viewer, params);
        // Zapisz parametry dla przyszłych przeładowań wariantów
        if (result && result.trimbleLoadUrl === undefined) {
          result.trimbleLoadUrl = url;
          result.trimbleLoadParams = params;
        }
        break;
      default:
        throw new Error(`Unknown format: ${format}`);
    }
    return result;
  }

  /**
   * Hybrid format detection - tries to detect format, falls back to 'generic'
   */
  static async detectFormat(url, imagesFileName) {
    const headers = this.getAuthHeaders();

    // 1. Check if Trimble format (RegistrationInfos.csv exists)
    try {
      const response = await fetch(`${url}/RegistrationInfos.csv`, { headers, method: 'HEAD' });
      if (response.ok) {
        return 'trimble';
      }
    } catch (e) {
      // Ignore - file doesn't exist or CORS issue
    }

    // 2. Check coordinates file for format hints
    try {
      const fileName = imagesFileName || 'coordinates';
      const response = await fetch(`${url}/${fileName}.txt`, { headers });
      if (response.ok) {
        const text = await response.text();
        const lines = text.split(/\r?\n/);
        const firstLine = lines[0].trim().toLowerCase();
        const secondLine = lines[1] || '';
        const columnCount = secondLine.split(/\t/).length;

        // Check first line for type declaration
        if (firstLine === 'leica') {
          return 'leica';
        }
        if (firstLine === 'navvis') {
          return 'navvis';
        }

        // Check column count - navvis has 9-10 columns (with quaternion/isHidden)
        if (columnCount >= 9) {
          return 'navvis';
        }

        // 8 or fewer columns - generic format
        return 'generic';
      }
    } catch (e) {
      console.warn('[Images360Loader] Could not detect format, using generic:', e);
    }

    // 3. Fallback to generic
    return 'generic';
  }

  /**
   * Get authentication headers
   */
  static getAuthHeaders() {
    const headers = {};
    if (window.viewerToken) {
      headers['Authorization'] = `Bearer ${window.viewerToken}`;
    }
    return headers;
  }

  /**
   * Generic format loader - original Potree format
   * File: coordinates.txt (TSV)
   * Columns: filename, time, long, lat, alt, course, pitch, roll (8 columns)
   */
  static async loadGeneric(url, imagesFileName, images360, viewer, params) {
    const headers = this.getAuthHeaders();
    const fileName = imagesFileName || 'coordinates';

    const response = await fetch(`${url}/${fileName}.txt`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load coordinates: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();

    const lines = text.split(/\r?\n/);
    const coordinateLines = lines.slice(1); // Skip header line

    let imagesSourceUnique = [];
    for (const line of coordinateLines) {
      if (line.trim().length === 0) continue;

      const tokens = line.split(/\t/);
      const [filename, time, long, lat, alt, course, pitch, roll] = tokens;

      const image360 = new Image360(
        `${url}/${filename.replace(/"/g, '')}`,
        parseFloat(time),
        parseFloat(long),
        parseFloat(lat),
        parseFloat(alt),
        parseFloat(course),
        parseFloat(pitch),
        parseFloat(roll),
        false, // real (no quaternion)
        'generic',
        false // isHidden
      );

      const xy = params.transform.forward([parseFloat(long), parseFloat(lat)]);
      image360.position = [...xy, parseFloat(alt)];
      image360.name = images360.cloudUuid ? `${images360.cloudUuid}/${fileName}` : fileName;

      images360.images.push(image360);
      if (!imagesSourceUnique.includes(image360.name)) {
        imagesSourceUnique.push(image360.name);
        images360._visible.push(true);
        images360.imagesSourceUnique.push(image360.name);
      }

      this.createSceneNodes(images360, image360, params.transform);
    }

    this.handleInitialFocus(images360, params);
    return images360;
  }

  /**
   * Navvis format loader
   * File: {name}.txt (TSV)
   * Columns: filename, time, long, lat, alt, course, pitch, roll, real, isHidden (10 columns)
   * real = quaternion w component
   */
  static async loadNavvis(url, imagesFileName, images360, viewer, params) {
    const headers = this.getAuthHeaders();
    const fileName = imagesFileName || 'coordinates';

    const response = await fetch(`${url}/${fileName}.txt`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load coordinates: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();

    const lines = text.split(/\r?\n/);
    const coordinateLines = lines.slice(1); // Skip type/header line
    const type = lines[0].trim() || 'navvis';

    let imagesSourceUnique = [];
    for (const line of coordinateLines) {
      if (line.trim().length === 0) continue;

      const tokens = line.split(/\t/);
      const [filename, time, long, lat, alt, course, pitch, roll, real, isHiddenStr] = tokens;

      // Parse quaternion w (real) and isHidden
      let realValue = parseFloat(real);
      let isHidden = isHiddenStr === 'true';

      // Handle case where real is actually isHidden (old format compatibility)
      if (isNaN(realValue)) {
        isHidden = real === 'true';
        realValue = false;
      }

      const image360 = new Image360(
        `${url}/${filename.replace(/"/g, '')}`,
        parseFloat(time),
        parseFloat(long),
        parseFloat(lat),
        parseFloat(alt),
        parseFloat(course),
        parseFloat(pitch),
        parseFloat(roll),
        realValue,
        type,
        isHidden
      );

      const xy = params.transform.forward([parseFloat(long), parseFloat(lat)]);
      image360.position = [...xy, parseFloat(alt)];
      image360.name = images360.cloudUuid ? `${images360.cloudUuid}/${fileName}` : fileName;

      images360.images.push(image360);
      if (!imagesSourceUnique.includes(image360.name)) {
        imagesSourceUnique.push(image360.name);
        images360._visible.push(true);
        images360.imagesSourceUnique.push(image360.name);
      }

      this.createSceneNodes(images360, image360, params.transform);
    }

    this.handleInitialFocus(images360, params);
    return images360;
  }

  /**
   * Leica format loader - same as Navvis but with different course offset
   * File: {name}.txt (TSV)
   * First line must be 'leica'
   */
  static async loadLeica(url, imagesFileName, images360, viewer, params) {
    // Leica uses the same file format as Navvis, just with type='leica'
    // The Image360 constructor handles the course offset difference
    return this.loadNavvis(url, imagesFileName, images360, viewer, params);
  }

  /**
   * Trimble format loader
   * File 1: RegistrationInfos.csv - CSV with filename, x, y, z, qx, qy, qz, qw
   * File 2: Station positions CSV - contains global coordinates of stations
   * 
   * Mapuje każdą panoramę do odpowiadającej stacji na podstawie numeru w nazwie.
   * Pozycja panoramy = pozycja stacji, orientacja = kwaternion z RegistrationInfos.csv
   */
  static async loadTrimble(url, images360, viewer, params) {
    // Wczytaj dane
    const { dataLines, stations } = await this.loadTrimbleData(url, params);

    // Mapuj panoramy do stacji
    return this.loadTrimblePanoramas(url, images360, viewer, params, stations, dataLines);
  }

  /**
   * Wspólna metoda do ładowania danych Trimble
   */
  static async loadTrimbleData(url, params) {
    const headers = this.getAuthHeaders();

    // 1. Load station positions
    const stationFile = params.stationFile || await this.findTrimbleStationFile(url);

    const stationResponse = await fetch(`${url}/${stationFile}`, { headers });
    if (!stationResponse.ok) {
      throw new Error(`Failed to load station positions: ${stationResponse.status}`);
    }
    const stationText = await stationResponse.text();
    const stations = this.parseTrimbleStations(stationText);

    // 2. Load registration info (panorama positions)
    const regResponse = await fetch(`${url}/RegistrationInfos.csv`, { headers });
    if (!regResponse.ok) {
      throw new Error(`Failed to load RegistrationInfos.csv: ${regResponse.status}`);
    }
    const regText = await regResponse.text();

    const lines = regText.split(/\r?\n/);
    const dataLines = lines.slice(1); // Skip header

    return { dataLines, stations };
  }

  /**
   * Wyciąga numer z nazwy stacji (np. "St1" → "1", "29_kolor" → "29", "1" → "1")
   */
  static extractStationNumber(stationName) {
    // Usuń prefiksy typu "St", "Station" itp.
    let cleaned = stationName.trim();

    // Usuń prefiks "St" jeśli istnieje
    cleaned = cleaned.replace(/^St/i, '');

    // Wyciągnij pierwszy ciąg cyfr
    const match = cleaned.match(/^(\d+)/);
    if (match) {
      return match[1];
    }

    // Jeśli nie ma cyfr, zwróć null
    return null;
  }

  /**
   * Mapowanie każdej panoramy do jej własnej stacji na podstawie numeru
   * Wyciąga numer z nazwy stacji (np. "St1" → "1", "29_kolor" → "29")
   * i dopasowuje do nazwy pliku panoramy (np. "1.jpeg", "29.jpeg")
   * Pozycja panoramy = pozycja stacji, orientacja = kwaternion z RegistrationInfos.csv
   */
  static loadTrimblePanoramas(url, images360, viewer, params, stations, dataLines) {

    // Przygotuj mapę: numer stacji -> stacja
    const stationNumberMap = {};
    for (const [stationName, station] of Object.entries(stations)) {
      const number = this.extractStationNumber(stationName);
      if (number) {
        if (stationNumberMap[number]) {
          console.warn(`[Images360Loader] Multiple stations with number '${number}': '${stationName}' and '${Object.keys(stationNumberMap[number])[0]}'`);
        }
        stationNumberMap[number] = { stationName, station };
      } else {
        console.warn(`[Images360Loader] Could not extract number from station name '${stationName}'`);
      }
    }

    let imagesSourceUnique = [];
    let matchedCount = 0;
    let unmatchedCount = 0;

    for (const line of dataLines) {
      if (line.trim().length === 0) continue;

      const tokens = line.split(',');
      const [filename, x, y, z, qx, qy, qz, qw] = tokens;

      // Wyciągnij numer z nazwy pliku (np. "1.jpeg" → "1", "10.jpeg" → "10")
      const filenameNumber = filename.trim().replace(/\.(jpeg|jpg|png)$/i, '');

      // Znajdź odpowiadającą stację po numerze
      const stationEntry = stationNumberMap[filenameNumber];

      if (!stationEntry) {
        console.warn(`[Images360Loader] No station found for panorama '${filename}' (number: '${filenameNumber}'). Available numbers: ${Object.keys(stationNumberMap).join(', ')}`);
        unmatchedCount++;
        continue; // Pomiń tę panoramę
      }

      const { stationName, station } = stationEntry;
      matchedCount++;

      // Użyj BEZPOŚREDNIO współrzędnych stacji (ignorujemy x,y,z z RegistrationInfos.csv)
      // Pozycja panoramy = pozycja stacji
      const globalX = station.x;
      const globalY = station.y;
      const globalZ = station.z;

      // Convert quaternion (qx, qy, qz, qw) to Euler angles (course, pitch, roll)
      const quaternion = new THREE.Quaternion(
        parseFloat(qx),
        parseFloat(qy),
        parseFloat(qz),
        parseFloat(qw)
      ).normalize();

      const euler = new THREE.Euler(0, 0, 0, 'YXZ').setFromQuaternion(quaternion);

      // 1. Course (obrót OZ): oblicz z azymutu między stacjami (układ globalny)
      //    Kwaternion reprezentuje orientację w układzie lokalnym, więc course lepiej obliczyć z pozycji
      const refStation = stations['1']; // Stacja referencyjna
      let course;

      if (refStation && stationName !== '1') {
        // Dla pozostałych stacji: oblicz azymut z różnicy współrzędnych globalnych
        const deltaX = station.x - refStation.x;
        const deltaY = station.y - refStation.y;
        course = THREE.MathUtils.radToDeg(Math.atan2(deltaY, deltaX));

        // Normalizuj do zakresu -180..180
        while (course > 180) course -= 360;
        while (course < -180) course += 360;
      } else {
        // Dla stacji 1: użyj kwaternionu (lub można obliczyć z innej referencji)
        course = THREE.MathUtils.radToDeg(euler._z);
      }

      // 2. Pitch i roll: wyciągnij z kwaternionu (pochylenia skanera)
      //    Skaner może nie być wypoziomowany, więc używamy wartości z kwaternionu
      const pitch = THREE.MathUtils.radToDeg(euler._y); // Obrót wokół osi Y
      const roll = THREE.MathUtils.radToDeg(euler._x);  // Obrót wokół osi X

      const image360 = new Image360(
        `${url}/${filename.trim()}`,
        0.0, // time - Trimble doesn't have this field
        globalX,
        globalY,
        globalZ,
        course,
        pitch,
        roll,
        false, // real - already converted from quaternion
        'trimble',
        false // isHidden
      );

      const xy = params.transform.forward([globalX, globalY]);
      image360.position = [...xy, globalZ];
      image360.name = images360.cloudUuid ? `${images360.cloudUuid}/trimble` : 'trimble';

      images360.images.push(image360);
      if (!imagesSourceUnique.includes(image360.name)) {
        imagesSourceUnique.push(image360.name);
        images360._visible.push(true);
        images360.imagesSourceUnique.push(image360.name);
      }

      this.createSceneNodes(images360, image360, params.transform);
    }

    this.handleInitialFocus(images360, params);
    return images360;
  }


  /**
   * Find Trimble station file (tries common naming patterns)
   */
  static async findTrimbleStationFile(url) {
    const headers = this.getAuthHeaders();
    const candidates = [
      'St2-Station pozitions.csv',
      'Station positions.csv',
      'stations.csv'
    ];

    for (const candidate of candidates) {
      try {
        const response = await fetch(`${url}/${candidate}`, { headers, method: 'HEAD' });
        if (response.ok) {
          return candidate;
        }
      } catch (e) {
        // Continue to next candidate
      }
    }

    throw new Error('Could not find Trimble station positions file. Tried: ' + candidates.join(', '));
  }

  /**
   * Parse Trimble station positions CSV
   */
  static parseTrimbleStations(text) {
    const stations = {};
    const lines = text.split(/\r?\n/).slice(1); // Skip header

    for (const line of lines) {
      if (line.trim().length === 0) continue;

      const tokens = line.split(',');
      const [name, x, y, z] = tokens;

      stations[name.trim()] = {
        x: parseFloat(x),
        y: parseFloat(y),
        z: parseFloat(z)
      };
    }

    return stations;
  }

  /**
   * Handle initial focus on specific image (from params.image)
   */
  static handleInitialFocus(images360, params) {
    if (params.image !== null && params.image !== '' && params.image !== undefined) {
      for (const [key, value] of Object.entries(images360.images)) {
        let file = '';
        if (value.file.lastIndexOf('./') === -1) {
          file += value.file.slice(value.file.lastIndexOf('/'), value.file.length);
        } else {
          file += value.file.split('./').pop();
        }
        if (file === params.image) {
          images360.focusHTML(images360.images[key], params.image);
          break;
        }
      }
    }
  }

  /**
   * Create scene nodes for an image360
   */
  static createSceneNodes(images360, image360, transform) {
    const { longitude, latitude, altitude } = image360;
    const xy = transform.forward([longitude, latitude]);
    const mesh = new THREE.Mesh(sphereGeometry, sphereMarkerMaterial);

    mesh.name = image360.file;
    mesh.position.set(...xy, altitude);
    mesh.scale.set(1, 1, 1);
    mesh.image360 = image360;

    {
      // orientation
      const { course, pitch, roll } = image360;
      mesh.rotation.set(
        THREE.MathUtils.degToRad(+roll + 90),
        THREE.MathUtils.degToRad(-pitch),
        THREE.MathUtils.degToRad(-course + 90),
        'ZYX'
      );
    }

    images360.node.add(mesh);
    image360.mesh = mesh;

    const editModeChecked = images360.getEditModeChecked();
    if (image360.isHidden && editModeChecked) {
      image360.mesh.material = new THREE.MeshBasicMaterial({
        side: THREE.BackSide,
        color: 0x002299,
      });
      image360.mesh.visible = images360.panoramaAllowedByActiveClip(image360);
    } else if (!image360.isHidden) {
      image360.mesh.visible = images360.panoramaAllowedByActiveClip(image360);
    } else {
      image360.mesh.visible = false;
    }
  }

}

